/**
 * ModuleLayout — Shared Sub-navigation layout for TMS / WMS / FMS modules
 *
 * Renders a horizontal tab-bar sub-nav beneath the main AppLayout topbar,
 * with optional AI agent status badges in the top-right corner.
 *
 * Navigation items are server-only when `moduleId` is provided:
 *   1. DB/server config — `moduleId` matches a NavigationCategory.id from useLayoutConfigContext()
 *   2. Empty — no props fallback for module navigation
 */

import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import type { LucideIcon } from 'lucide-react';
import { Bot, Zap } from 'lucide-react';

type IconComponent = LucideIcon | React.ComponentType<{ className?: string }>;

import { useLayoutConfigContext } from '@/lib/use-layout-config';
import { resolveIcon } from '@/lib/icon-map';

export interface ModuleNavItem {
  label: string;
  path: string;
  icon?: IconComponent;
  badge?: string;
  /** Optional group key — a visual separator is inserted between adjacent groups */
  group?: string;
}

export interface AgentStatus {
  id: string;
  name: string;
  status: 'active' | 'idle' | 'processing';
  label?: string;
}

interface ModuleLayoutProps {
  title: string;
  subtitle?: string;
  icon?: IconComponent;
  accentColor?: string;     // Tailwind class e.g. 'text-blue-600'
  contentClassName?: string;
  /** Legacy explicit nav items — ignored when moduleId is provided */
  navItems?: ModuleNavItem[];
  /** Category ID to load nav items from DB config (e.g. 'freight', 'transport', 'warehouse') */
  moduleId?: string;
  /** Show compact group labels in the sub-navigation bar. */
  showGroupLabels?: boolean;
  /** Disable the horizontal second-level sub-navigation. */
  showSubnav?: boolean;
  agents?: AgentStatus[];
  children?: React.ReactNode;
}

const statusColor: Record<AgentStatus['status'], string> = {
  active:     'bg-green-500',
  idle:       'bg-gray-400',
  processing: 'bg-amber-400 animate-pulse',
};

function formatGroupLabel(group?: string): string {
  if (!group) return '';
  const labels: Record<string, string> = {
    relationship: 'Accounts',
    'revenue-ops': 'Revenue',
  };
  if (labels[group]) return labels[group];
  return group
    .split(/[-_]/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function normalizeModuleNavPath(moduleId: string | undefined, path: string): string {
  if (moduleId === 'rm') return path.replace(/^\/crm\b/, '/rm');
  return path;
}

export default function ModuleLayout({
  title,
  subtitle,
  icon: Icon,
  accentColor = 'text-primary',
  contentClassName,
  navItems: propNavItems,
  moduleId,
  showGroupLabels = false,
  showSubnav = true,
  agents = [],
}: ModuleLayoutProps) {
  const location = useLocation();
  const { config } = useLayoutConfigContext();
  const { t } = useTranslation();

  // Resolve nav items from server config only for module-bound layouts.
  const navItems = useMemo(() => {
    if (moduleId && config.navigation) {
      const cat = config.navigation.find((c) => c.id === moduleId);
      if (cat && cat.items.length > 0) {
        return cat.items.map((item): ModuleNavItem => ({
          label: item.labelKey ? t(item.labelKey, item.label) : t(`nav.${item.label}`, item.label),
          path: normalizeModuleNavPath(moduleId, item.route),
          icon: resolveIcon(item.icon),
          group: item.group,
        }));
      }
      return [];
    }
    return (propNavItems ?? []).map((item) => ({
      ...item,
      label: t(`nav.${item.label}`, item.label),
    }));
  }, [moduleId, config.navigation, propNavItems, t]);

  const isPlannerSurface = /\/(planner|dispatch)$/.test(location.pathname);
  const activeNavPath = useMemo(() => {
    const exactMatch = navItems.find((item) => item.path === location.pathname);
    if (exactMatch) return exactMatch.path;

    const prefixMatches = navItems
      .filter((item) => item.path !== '/' && location.pathname.startsWith(`${item.path}/`))
      .sort((left, right) => right.path.length - left.path.length);

    return prefixMatches[0]?.path ?? null;
  }, [location.pathname, navItems]);

  return (
    <div className="flex flex-col min-h-0 flex-1">
      {/* ── Module Header ──────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-4 pt-4 pb-0 gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {Icon && (
            <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-background border shadow-sm ${accentColor}`}>
              <Icon className="h-5 w-5" />
            </div>
          )}
          <div className="min-w-0">
            <h1 className={`text-xl font-bold leading-none ${accentColor}`}>{title}</h1>
            {subtitle && (
              <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{subtitle}</p>
            )}
          </div>
        </div>

        {/* AI Agent badges */}
        {agents.length > 0 && (
          <div className="flex items-center gap-1.5 shrink-0">
            <Bot className="h-3.5 w-3.5 text-muted-foreground" />
            {agents.map((agent) => (
              <div
                key={agent.id}
                className="flex items-center gap-1 rounded-full border bg-background px-2 py-0.5 text-[10px] font-medium"
                title={`${agent.name} — ${agent.status}`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${statusColor[agent.status]}`} />
                <span className="text-muted-foreground">{agent.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Sub-navigation Tab Bar ──────────────────────────────────────── */}
      {showSubnav && navItems.length > 0 && (
        <div className="mt-3 border-b px-4">
          <nav className="flex gap-0 overflow-x-auto -mb-px">
            {navItems.map((item, idx) => {
              const ItemIcon = item.icon;
              const isActive = item.path === activeNavPath;
              const prevGroup = idx > 0 ? navItems[idx - 1]?.group : item.group;
              const showSep = idx > 0 && item.group !== prevGroup;
              return (
                <Fragment key={item.path}>
                  {showSep && (
                    <div className="mx-1 self-stretch border-l border-border/40" />
                  )}
                  {showGroupLabels && (idx === 0 || showSep) && item.group && (
                    <span className="self-center px-2 text-[10px] font-semibold uppercase text-muted-foreground">
                      {formatGroupLabel(item.group)}
                    </span>
                  )}
                  <NavLink
                    to={item.path}
                    end={item.path === location.pathname}
                    className={cn(
                      'flex items-center gap-1.5 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors',
                      showGroupLabels ? 'px-2' : 'px-3',
                      isActive
                        ? 'border-primary text-primary'
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border',
                    )}
                  >
                    {ItemIcon && <ItemIcon className="h-3.5 w-3.5" />}
                    {item.label}
                    {item.badge && (
                      <Badge variant="secondary" className="ml-1 h-4 px-1 text-[9px]">
                        {item.badge}
                      </Badge>
                    )}
                  </NavLink>
                </Fragment>
              );
            })}
          </nav>
        </div>
      )}

      {/* ── Page Content ───────────────────────────────────────────────── */}
      <div className={cn(
        'flex-1 min-h-0',
        isPlannerSurface ? 'overflow-hidden p-0' : 'overflow-auto p-4',
        contentClassName,
      )}>
        <Outlet />
      </div>
    </div>
  );
}
